import { useLocation, useNavigate } from "react-router-dom";
import { Globe } from "lucide-react";
import { useLang, stripLocale, localePath, useAltPaths } from "@/i18n";
import { cn } from "@/lib/utils";

interface Props {
  className?: string;
  onNavigate?: () => void;
}

/** DE / EN toggle. Keeps the current page and honours language-specific slugs. */
const LanguageSwitcher = ({ className, onNavigate }: Props) => {
  const lang = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const { altPaths } = useAltPaths();

  const currentGerman = altPaths?.de ?? stripLocale(location.pathname);
  const englishTarget = altPaths ? altPaths.en : stripLocale(location.pathname);

  const go = (target: "de" | "en") => {
    if (target === lang) return;
    if (target === "de") {
      navigate(currentGerman + location.hash);
    } else {
      navigate(localePath("en", englishTarget ?? "/") + location.hash);
    }
    onNavigate?.();
  };

  const btn = (code: "de" | "en", label: string, title: string) => (
    <button
      type="button"
      onClick={() => go(code)}
      aria-label={title}
      title={title}
      aria-current={lang === code ? "true" : undefined}
      className={cn(
        "px-2 py-0.5 text-xs font-semibold uppercase tracking-wide rounded-full transition-colors",
        lang === code
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-primary",
      )}
    >
      {label}
    </button>
  );

  return (
    <div className={cn("inline-flex items-center gap-1 rounded-full border border-border px-1 py-0.5", className)}>
      <Globe className="w-3.5 h-3.5 text-muted-foreground ml-1" aria-hidden="true" />
      {btn("de", "DE", "Auf Deutsch ansehen")}
      {btn("en", "EN", "View in English")}
    </div>
  );
};

export default LanguageSwitcher;
